import React, { useEffect, useState } from "react";
import { change, getFormValues } from "redux-form";
import { FORM_NAMES } from "../../constants/forms";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../types/redux";
import { DisplaySectionFieldType, DisplaySectionType } from "../../types/components";
import { useHistory } from "react-router-dom";

import DisplaySection from "../../components/MoneyTransfer/DisplaySection";
import { BankAccountChildProps } from "../../types/props";
import { BankAccountFormValues } from "../../types/forms";
import { BANK_ACCOUNT_FLOW_TYPES, BANK_ACCOUNT_FORM_FIELDS, COUNTRY_IDS, FIELD_TYPES_BY_NAME, ICONS, REVIEW_BANK_ACCOUNT_DETAILS } from "../../constants/enums";
import i18n from "../../i18n/config";
import { ROUTES } from "../../constants/clientRoutes";
import { generateUniqueInteger } from "../../services/formatter";
import { DropdownType } from "../../types/fields";
import Alert from "../../components/Common/Alerts/Alert";

const ReviewAccountDetails = (props: BankAccountChildProps) => {
  const { setTitle, setSubTitle, setForwardButton } = props;
  const history = useHistory();
  const dispatch = useDispatch();
  const formValues = useSelector((state: RootState) => getFormValues(FORM_NAMES.BANK_ACCOUNTS)(state)) as BankAccountFormValues;
  const { destinationCountryName, destinationCurrencyName, natureOfRelationshipOptions, beneficiaryCountryOptions, residentialStatusOptions, bankAccountTypeOptions, type } = useSelector((state: RootState) => state.bankAccounts);
  const [sections, setSections] = useState<DisplaySectionType[]>([]);
  const [checkboxChecked, setCheckboxChecked] = useState(false);
  const forwardButtonInitialState = { text: type === BANK_ACCOUNT_FLOW_TYPES.NEW ? i18n.t("PAGES.BANK_ACCOUNTS.REVIEW_ACCOUNT_DETAILS.BUTTON_TEXT.ADD") : i18n.t("PAGES.BANK_ACCOUNTS.REVIEW_ACCOUNT_DETAILS.BUTTON_TEXT.EDIT"), clickFunc: () => history.push(ROUTES.BANKS_ACCOUNTS.COMMUNICATION_PREFERENCES), disabled: true }
  const [isUSA, setIsUSA] = useState(false);
  const [isMexico, setIsMexico] = useState(false);

  /**
   * On initial render set the title, subtitle, back button, and forward button
   */
  useEffect(() => {
    setTitle(i18n.t("PAGES.BANK_ACCOUNTS.REVIEW_ACCOUNT_DETAILS.TITLE"));
    setSubTitle(i18n.t(""));
    setForwardButton(forwardButtonInitialState);
    mapFormValuesToDisplaySections();

    // If the bank country is USA, set the isUSA state to true to determine labels for the bank details
    if (formValues[BANK_ACCOUNT_FORM_FIELDS.BANK_COUNTRY] === COUNTRY_IDS.USA) {
      setIsUSA(true);
    }

    // If the bank country is Mexico, set the isMexico state to true to determine labels for the bank details
    if (formValues[BANK_ACCOUNT_FORM_FIELDS.BANK_COUNTRY] === COUNTRY_IDS.MEXICO) {
      setIsMexico(true);
    }
  }, []);

  /**
   * Disable forward button if checkbox is not checked
   */
  useEffect(() => {
    if (checkboxChecked) {
      setForwardButton({
        ...forwardButtonInitialState,
        disabled: false
      });
    } else {
      setForwardButton(forwardButtonInitialState);
    }
  }, [checkboxChecked]);

  /**
   * Map form field id to DisplaySection label
   */
  const beneficiaryFieldMap = {
    BeneficiaryName: "Name",
    BeneficiaryStreetAddress: "Address",
    BeneficiaryStateProvince: isUSA ? "State" : "Province",
    BeneficiaryPostalCode: isUSA ? "Zip Code" : "Postal Code",
    BeneficiaryCity: "City",
    BeneficiaryCountry: "Country",
    BeneficiaryPhoneNumber: "Phone Number",
    BeneficiaryContactName: "Contact Name",
    BeneficiaryPassportNumber: "Passport Number",
    BeneficiaryIDCode: "ID Number",
    BeneficiaryTaxID: "Tax ID",
    BeneficiaryContactName2: "Patronymic Name",
    ResidentialStatus: "Residential Status",
    BeneficiaryTypeId: "Beneficiary Type",
    NatureofRelationship: "Nature of Relationship",
    SpecifyVat: "Specify VAT"
  }

  /**
   * Map form field id to DisplaySection label
   */
  const bankFieldMap = {
    BankName: "Name",
    BankStreetAddress: "Address",
    BankAccountType: "Account Type",
    BankCity: "City",
    BankStateProvince: isUSA ? "State" : "Province",
    BankPostalCode: isUSA ? "Zip Code" : "Postal Code",
    BankCountry: "Country",
    BankAccount: "Account Number",
    BankRoutingCode: "Local Routing Code",
    SwiftBranchDetails: isMexico ? "13-digit RFC or 18-digit CURP Code" : "SWIFT Branch Details",
    BankSwiftAddress: "SWIFT Address",
  }

  const beneficiaryDisplaySection: DisplaySectionType = {
    id: "Beneficiary",
    label: i18n.t("PAGES.BANK_ACCOUNTS.REVIEW_ACCOUNT_DETAILS.RECIPIENT_HEADING"),
    name: "Beneficiary",
    fields: []
  }

  const bankDisplaySection: DisplaySectionType = {
    id: "Bank",
    label: i18n.t("PAGES.BANK_ACCOUNTS.REVIEW_ACCOUNT_DETAILS.BANK_HEADING"),
    name: "Bank",
    fields: []
  }

  /**
   * Use function to generate unique integer id for horizontal rule field
   */
  const getHorizontalRuleField = () => {
    const id = generateUniqueInteger();
    return {
      id,
      label: "",
      formattedValue: "",
      originalFormattedValue: "",
      disclaimerId: "",
      superscript: "",
      type: FIELD_TYPES_BY_NAME.HORIZONTAL_RULE
    }
  }

  /**
   * For a few fields, we need to get the formatted value from the redux store
   * @param field 
   * @param key 
   * @param value 
   * @returns 
   */
  const getFormattedValueFromRedux = (field: DisplaySectionFieldType, key: string, value: string) => {
    if (key === BANK_ACCOUNT_FORM_FIELDS.NATURE_OF_RELATIONSHIP) {
      const option = natureOfRelationshipOptions.find((option: DropdownType) => option.value === value);
      field.formattedValue = option?.label;
    }

    if (key === BANK_ACCOUNT_FORM_FIELDS.BENEFICIARY_COUNTRY) {
      const option = beneficiaryCountryOptions.find((option: DropdownType) => option.value === value);
      field.formattedValue = option?.label;
    }

    if (key === BANK_ACCOUNT_FORM_FIELDS.RESIDENTIAL_STATUS) {
      const option = residentialStatusOptions.find((option: DropdownType) => option.value === value);
      field.formattedValue = option?.label;
    }

    if (key === BANK_ACCOUNT_FORM_FIELDS.BANK_ACCOUNT_TYPE) {
      const option = bankAccountTypeOptions.find((option: DropdownType) => option.value === value);
      field.formattedValue = option?.label;
    }

    if (key === BANK_ACCOUNT_FORM_FIELDS.BENEFICIARY_PHONE_NUMBER) {
      // If phone number is optional and user types in phone country or number and then deletes it, redux-form still has the BeneficiaryPhoneNumber field value exist as { number: "" }
      // So we need to check if the phone number actually has a value or not, and then set the formatted value accordingly
      // We also need to change the redux-form value to null if the phone number is empty so that there are no issues on submit
      if (field?.formattedValue?.country?.phoneCode && field?.formattedValue?.number) {
        field.formattedValue = `+${field.formattedValue.country.phoneCode} ${field.formattedValue.number}`;
      } else {
        dispatch(change(FORM_NAMES.BANK_ACCOUNTS, BANK_ACCOUNT_FORM_FIELDS.BENEFICIARY_PHONE_NUMBER, ""));
        field.formattedValue = null;
      }
    }

    return field;
  }

  const mapFormValuesToDisplaySections = () => {
    let index = 0;

    for (const [key, value] of Object.entries(formValues)) {
      index++;
      let field = {
        id: index,
        label: "",
        formattedValue: value,
        originalFormattedValue: value,
        disclaimerId: "",
        superscript: "",
        type: "text"
      }

      if (!field.formattedValue) {
        continue;
      }

      if (REVIEW_BANK_ACCOUNT_DETAILS.BENEFICIARY_FIELD_NAMES.includes(key)) {
        field.label = beneficiaryFieldMap[key];

        field = getFormattedValueFromRedux(field, key, value);

        if (field.formattedValue) {
          beneficiaryDisplaySection.fields.push(field);
          beneficiaryDisplaySection.fields.push(getHorizontalRuleField());
        }

      } else if (REVIEW_BANK_ACCOUNT_DETAILS.BANK_FIELD_NAMES.includes(key)) {
        field.label = bankFieldMap[key];

        if (key === "BankCountry") {
          field.formattedValue = destinationCountryName;
        }

        bankDisplaySection.fields.push(field);
        bankDisplaySection.fields.push(getHorizontalRuleField());
      }
    }

    // Remove last horizontal rule
    beneficiaryDisplaySection.fields.pop();
    bankDisplaySection.fields.pop();

    setSections([beneficiaryDisplaySection, bankDisplaySection]);
  }

  return (
    <>
      <p className="bold">{i18n.t("COMMON.CURRENCY")}: {destinationCurrencyName}</p>
      <DisplaySection sections={sections} className="remove-p-margin" />
      <Alert
        alertClasses="alert-info alert-bkgrd-white flex-row align-items-start text-left"
        icon={ICONS.INFO}
        iconClasses={"spacing-right-tiny"}
      >
        <p className="bold">{i18n.t("PAGES.BANK_ACCOUNTS.REVIEW_ACCOUNT_DETAILS.ALERT.HEADING")}</p>
        <p className="spacing-bottom-none">{i18n.t("PAGES.BANK_ACCOUNTS.REVIEW_ACCOUNT_DETAILS.ALERT.P1")}</p>
      </Alert>
      <div className="checkbox spacing-top-small spacing-bottom-medium bold">
        <input
          type="checkbox"
          id="confirmDetails"
          onChange={() => setCheckboxChecked(!checkboxChecked)}
        />{" "}
        <label htmlFor="confirmDetails">{i18n.t("PAGES.BANK_ACCOUNTS.REVIEW_ACCOUNT_DETAILS.CHECKBOX")}</label>
      </div>
    </>

  );
}

export default ReviewAccountDetails;